import { isHouseImplemented } from "@nano/game-config";
import type { HouseGameModule, HouseLevels } from "./contracts.ts";

type HouseWithModule = keyof HouseLevels;
type Loaders = { [H in HouseWithModule]: () => Promise<HouseGameModule<HouseLevels[H]>> };

/** Only houses with gameplay code have a loader; the rest stay as metadata. */
const LOADERS: Loaders = {
  sorting: () => import("./sorting/module.ts").then((m) => m.createSortingModule()),
  colors: () => import("./colors/module.ts").then((m) => m.createColorLabModule()),
  tiles: () => import("./tiles/module.ts").then((m) => m.createJadePathModule()),
  logic: () => import("./logic/module.ts").then((m) => m.createLogicTowerModule()),
  fill: () => import("./fill/module.ts").then((m) => m.createMagicWorkshopModule()),
  arcade: () => import("./arcade/module.ts").then((m) => m.createClawModule()),
};

const instances = new Map<HouseWithModule, Promise<HouseGameModule<unknown>>>();

/** Loads a house module once; houses without gameplay code never load. */
export function loadHouseModule<H extends HouseWithModule>(id: H): Promise<HouseGameModule<HouseLevels[H]>> {
  if (!isHouseImplemented(id)) return Promise.reject(new Error(`house ${id} has no gameplay yet`));
  let instance = instances.get(id) as Promise<HouseGameModule<HouseLevels[H]>> | undefined;
  if (!instance) {
    instance = LOADERS[id]() as Promise<HouseGameModule<HouseLevels[H]>>;
    instance.catch(() => instances.delete(id));
    instances.set(id, instance as Promise<HouseGameModule<unknown>>);
  }
  return instance;
}
